import type { PulseTracking } from "@m10s/search-frontend-clients/quest";
import { RelatedSearch } from "src/types/RelatedSearch";
import { SemanticAd } from "src/types/SemanticAd";
import { SemanticFilter, SemanticFilterItem } from "src/types/SemanticFilter";
import { ErrorState } from "../stores/searchStore";

export type SearchResponse = {
	results: SemanticAd[];
	filters?: SemanticFilter[];
	error?: ErrorState;
	tracking?: PulseTracking;
};

export type RelatedSearchResponse = {
	results: RelatedSearch[];
	error?: ErrorState;
};

type FilterParams = Record<string, unknown>;

function buildTrackingFromFilters(
	appliedFilters: FilterParams,
	apiFilters: SemanticFilter[],
): PulseTracking | undefined {
	const selectionFilters: Array<{ name: string; value: string; valueId?: string }> = [];

	Object.entries(appliedFilters).forEach(([filterName, filterValue]) => {
		if (!filterValue) return;

		const filterDef = apiFilters.find((f) => f.name === filterName);

		const values = Array.isArray(filterValue) ? filterValue : [filterValue];

		values.forEach((val) => {
			const valueStr = String(val);

			const option = filterDef?.filter_items?.find(
				(opt: SemanticFilterItem) => String(opt.value) === valueStr || String(opt.name) === valueStr,
			);

			selectionFilters.push({
				name: filterDef?.display_name || filterName,
				value: option?.display_name || option?.name || valueStr,
				valueId: valueStr,
			});
		});
	});

	if (selectionFilters.length === 0) return undefined;

	return {
		object: {
			selectionFilters,
		},
	} as PulseTracking;
}

export async function fetchSemanticSuggestions(
	q: string,
	limit?: number,
	filters?: FilterParams,
	eqe?: boolean,
): Promise<SearchResponse> {
	if (!q) return { results: [], filters: [] };

	const apiPath = "/recommerce-explore/api/semantic/search";
	const url = new URL(apiPath, window.location.origin);

	url.searchParams.set("q", q);
	if (limit) url.searchParams.set("limit", String(limit));
	if (filters) {
		Object.entries(filters).forEach(([key, value]) => {
			if (Array.isArray(value)) {
				if (value.length === 0) return;
				url.searchParams.delete(key);
				value.forEach((v) => {
					if (v !== undefined && v !== null && v !== "") {
						url.searchParams.append(key, String(v));
					}
				});
			} else if (value !== undefined && value !== null && value !== "") {
				url.searchParams.set(key, String(value));
			}
		});
	}
	if (eqe === false) {
		url.searchParams.set("eqe", "false");
	}

	try {
		const res = await fetch(url.toString());

		if (!res.ok) {
			console.error("semantic proxy error", res.status, res.statusText);

			const errorType = res.status >= 500 ? "server" : res.status >= 400 ? "client" : "network";

			return {
				results: [],
				filters: [],
				error: {
					hasError: true,
					errorType,
					statusCode: res.status,
				},
			};
		}

		const data = await res.json();
		// The API returns { id, docs: [...], filters, metadata } - extract the docs array
		const results = Array.isArray(data) ? data : data.docs || [];
		const apiFilters = Array.isArray(data?.filters) ? data.filters : [];

		const tracking = buildTrackingFromFilters(filters || {}, apiFilters);

		return { results, filters: apiFilters, tracking };
	} catch (err) {
		console.error("Failed to fetch semantic suggestions", err);
		return {
			results: [],
			filters: [],
			error: {
				hasError: true,
				errorType: "network",
			},
		};
	}
}

export async function fetchRelatedSearches(q?: string, limit: number = 10): Promise<RelatedSearchResponse> {
	const apiPath = "/recommerce-explore/api/semantic/related";
	const url = new URL(apiPath, window.location.origin);

	// Query is optional - API will return results even without a query
	if (q) {
		url.searchParams.set("q", q);
	}
	url.searchParams.set("limit", String(limit));

	try {
		const res = await fetch(url.toString());

		if (!res.ok) {
			console.error("related searches proxy error", res.status, res.statusText);

			const errorType = res.status >= 500 ? "server" : res.status >= 400 ? "client" : "network";

			return {
				results: [],
				error: {
					hasError: true,
					errorType,
					statusCode: res.status,
				},
			};
		}

		const data = await res.json();
		// The API returns { id, suggestion_type, search_term_suggestions: [{ search_term }] }
		const searchTermSuggestions = data.search_term_suggestions || [];
		const results: RelatedSearch[] = searchTermSuggestions.map((item: { search_term: string }) => ({
			query: item.search_term,
		}));
		return { results };
	} catch (err) {
		console.error("Failed to fetch related searches", err);
		return {
			results: [],
			error: {
				hasError: true,
				errorType: "network",
			},
		};
	}
}

export async function postSemanticFeedback(q: string, given_feedback: string): Promise<string | null> {
	if (!q) return null;

	const apiPath = "/recommerce-explore/api/semantic/feedback";
	const url = new URL(apiPath, window.location.origin);

	url.searchParams.set("q", q);
	url.searchParams.set("given_feedback", given_feedback);

	try {
		const res = await fetch(url.toString());

		if (!res.ok) {
			console.error("Semantic feedback API returned error", {
				status: res.status,
				statusText: res.statusText,
				query: q,
				feedback: given_feedback,
				url: url.toString(),
			});
			return null;
		}

		const data = await res.json();

		if (!data.feedback_url) {
			console.error("Feedback API response missing feedback_url", {
				response: data,
				query: q,
				feedback: given_feedback,
			});
			return null;
		}

		return data.feedback_url;
	} catch (err) {
		console.error("Unexpected error posting semantic feedback", {
			error: err,
			query: q,
			feedback: given_feedback,
			errorType: err instanceof Error ? err.name : typeof err,
			errorMessage: err instanceof Error ? err.message : String(err),
		});
		return null;
	}
}
